import { PropsWithChildren, MouseEvent, ReactNode } from 'react';
import StyledButton from '../styled/StyledButton';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/i18n';

interface ModalProps {
  closeModal: () => void;
  title: string;
  actionTitle: string;
  actionOnClick: () => void;
  headerNode?: ReactNode;
  message?: string;
  isActionDisabled?: boolean;
  specialColor?: 'success' | 'error';
  shouldCloseModalAfterAction?: boolean;
  hasAction?: boolean;
  cancelBtnLabel?: string;
  containerClassName?: string;
}

const Modal = ({
  closeModal,
  title,
  message,
  actionTitle,
  actionOnClick,
  children,
  isActionDisabled = false,
  headerNode,
  specialColor,
  shouldCloseModalAfterAction = true,
  hasAction = true,
  cancelBtnLabel,
  containerClassName,
}: PropsWithChildren<ModalProps>) => {
  const { t } = useTranslation();
  const defaultCancelLabel = t('common.cancel');

  const handleDialogClick = (event: MouseEvent<HTMLDialogElement>) => {
    if (event.target === event.currentTarget) {
      closeModal();
    }
  };

  return (
    <dialog
      open
      onClick={handleDialogClick}
      className="fixed left-0 top-0 z-50 flex h-full w-full items-center justify-center overflow-auto bg-black/50"
    >
      <div
        className={cn(
          'flex flex-col min-w-96 max-w-lg max-h-[90vh] gap-6 p-6 border rounded-md border-gray-200 bg-white',
          containerClassName,
        )}
      >
        <div className="flex items-center justify-between shrink-0">
          <h1 className="text-lg leading-6 font-semibold text-gray-950">
            {title}
          </h1>
          {headerNode}
        </div>
        <div className="flex flex-col gap-6 overflow-y-auto min-h-0">
          <p className="text-sm leading-6 text-gray-800 whitespace-pre-wrap max-w-96">
            {message}
          </p>
          {children}
        </div>
        <div className="flex gap-3 w-full shrink-0">
          <StyledButton
            variant="outlined"
            title={cancelBtnLabel || defaultCancelLabel}
            className="text-gray-800 border-gray-300 w-full"
            onClick={closeModal}
          />
          {hasAction && (
            <StyledButton
              title={actionTitle}
              disabled={isActionDisabled}
              onClick={() => {
                actionOnClick();
                if (shouldCloseModalAfterAction) closeModal();
              }}
              className={cn('w-full', {
                'bg-success-600 border-success-700 hover:bg-success-700':
                  specialColor === 'success',
                'bg-error-600 border-error-700 hover:bg-error-700':
                  specialColor === 'error',
              })}
            />
          )}
        </div>
      </div>
    </dialog>
  );
};

export default Modal;
